import PocketBase, { RecordListOptions } from 'pocketbase';
import { config } from '~/config';
import { RecordModel } from 'pocketbase';
import { CollectionRecords, CollectionResponses, TypedPocketBase } from '~/types/pb';
import { useRecordStore } from './useRecordStore';
import { computed, onBeforeUnmount, Ref, ref, watch } from 'vue';

const client = new PocketBase(config.apiUrl || undefined) as TypedPocketBase;
client.autoCancellation(false);

type Store<T extends keyof CollectionRecords> = ReturnType<typeof useRecordStore<CollectionRecords[T] & RecordModel>>;
const recordsStores: { [Key in keyof CollectionRecords]?: Store<Key> } = {};

export function useApi() {
  function loadRecordsStore(collection: keyof CollectionRecords) {
    if (!recordsStores[collection]) {
      recordsStores[collection] = useRecordStore(client, collection) as Store<any>;
    }
  }

  function col<T extends keyof CollectionResponses, S = CollectionResponses[T]>(collection: T) {
    loadRecordsStore(collection);
    const store = recordsStores[collection] as Store<T>;
    const c = client.collection(collection);
    return {
      create: c.create.bind(c)<S>,
      update: c.update.bind(c)<S>,
      delete: c.delete.bind(c),
      getOne: store.getOne,
      getList: store.getList,
      getFirstFromList: store.getFirstFromList,
    };
  }

  return Object.assign(client, { col });
}

export function useCollection<T extends keyof CollectionResponses, S = CollectionResponses[T]>(
  collection: T,
  options?: {
    filter?: Ref<string>;
    page?: Ref<number>;
    perPage?: Ref<number>;
    sort?: Ref<string>;
  },
  items = ref<S[]>(),
): {
  items: Ref<S[] | undefined>;
  loading: Ref<boolean>;
  total: Ref<number>;
  refresh: () => Promise<void>;
} {
  const api = useApi();

  const loading = ref(false);
  const total = ref(0);
  const page = computed(() => options?.page?.value ?? 1);
  const perPage = computed(() => options?.perPage?.value ?? 10);
  const filter = computed(() => options?.filter?.value);
  const sort = computed(() => options?.sort?.value);

  async function load() {
    loading.value = true;

    const listOptions: RecordListOptions = {};
    if (filter.value) {
      listOptions.filter = filter.value;
    }
    if (sort.value) {
      listOptions.sort = sort.value;
    }
    listOptions.perPage = options?.perPage?.value ?? 10;

    const response = await api.collection(collection).getList<S>(page.value, perPage.value, listOptions);
    items.value = response.items;
    total.value = response.totalItems;

    loading.value = false;
  }

  const watchHandle = watch([filter, page, sort, perPage], load, { immediate: true });
  onBeforeUnmount(() => {
    watchHandle.stop();
  });

  return {
    items,
    loading,
    total,
    refresh: load,
  };
}
